// =============================================================================
// SHANIZ GAMING ADMIN - NOTIFICATIONS BELL DROPDOWN
// =============================================================================
// Subscribes to the admin websocket via useAdminSocket. Keeps an in-memory
// ring buffer of the last 20 alerts. Unread badge on the bell. Per-item
// dismiss plus "Mark all read" footer.
// =============================================================================

import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  Tooltip,
  Typography,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useAdminSocket } from '../hooks/useAdminSocket';

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface AdminAlert {
  id: string;
  severity: AlertSeverity;
  title: string;
  message: string;
  createdAt: string; // ISO timestamp
}

const MAX_ALERTS = 20;

// Inline "X ago" helper — avoids adding a date-fns dependency.
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diff = Date.now() - then;
  const s = Math.max(0, Math.floor(diff / 1000));
  if (s < 45) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function severityColor(sev: AlertSeverity): string {
  switch (sev) {
    case 'critical':
      return '#EF4444';
    case 'warning':
      return '#F59E0B';
    case 'info':
    default:
      return '#3B82F6';
  }
}

function normalizeSeverity(raw?: string): AlertSeverity {
  const v = (raw || '').toLowerCase();
  if (v === 'critical' || v === 'error' || v === 'high' || v === 'severe') return 'critical';
  if (v === 'warning' || v === 'warn' || v === 'medium') return 'warning';
  return 'info';
}

const NotificationsBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [alerts, setAlerts] = useState<AdminAlert[]>([]);
  const [unread, setUnread] = useState(0);

  // Use the existing hook's listener signature — the backend does not emit a
  // literal `admin:alert` event type, so we synthesize one from the existing
  // `notification` and `fraud.alert` types.
  useAdminSocket((ev) => {
    if (ev.type === 'notification' && ev.payload.id !== 'ws-open') {
      const alert: AdminAlert = {
        id: ev.payload.id || `n-${Date.now()}`,
        severity: normalizeSeverity(ev.payload.level),
        title: ev.payload.title || 'Notification',
        message: ev.payload.body || '',
        createdAt: ev.payload.at || new Date().toISOString(),
      };
      setAlerts((arr) => [alert, ...arr].slice(0, MAX_ALERTS));
      setUnread((u) => u + 1);
    } else if (ev.type === 'fraud.alert') {
      const alert: AdminAlert = {
        id: ev.payload.id,
        severity: normalizeSeverity(ev.payload.severity),
        title: ev.payload.title,
        message: `User ${ev.payload.userId} flagged (${ev.payload.severity})`,
        createdAt: ev.payload.createdAt,
      };
      setAlerts((arr) => [alert, ...arr].slice(0, MAX_ALERTS));
      setUnread((u) => u + 1);
    }
  });

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setUnread(0);
  };

  const handleClose = () => setAnchorEl(null);

  const dismissOne = (id: string) => {
    setAlerts((arr) => arr.filter((a) => a.id !== id));
  };

  const markAllRead = () => {
    setAlerts([]);
    setUnread(0);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
          color="inherit"
          onClick={handleOpen}
        >
          <Badge
            badgeContent={unread}
            color="error"
            invisible={unread === 0}
            overlap="circular"
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 500,
            bgcolor: '#141414',
            color: '#F5F5F5',
            border: '1px solid #262626',
          },
        }}
        MenuListProps={{ sx: { py: 0 }, dense: true }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #262626',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Alerts
          </Typography>
          <Typography variant="caption" sx={{ color: '#A3A3A3' }}>
            {alerts.length} shown
          </Typography>
        </Box>

        {alerts.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#A3A3A3' }}>
              No alerts
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 380, overflowY: 'auto' }} dense>
            {alerts.map((a) => (
              <ListItem
                key={a.id}
                onClick={() => dismissOne(a.id)}
                sx={{
                  alignItems: 'flex-start',
                  borderBottom: '1px solid #1f1f1f',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#1A1A1A' },
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    mt: 0.75,
                    mr: 1.25,
                    flexShrink: 0,
                    bgcolor: severityColor(a.severity),
                    boxShadow: `0 0 0 3px ${severityColor(a.severity)}33`,
                  }}
                />
                <ListItemText
                  primary={a.title}
                  secondary={
                    <>
                      <span style={{ display: 'block', color: '#A3A3A3', fontSize: 12 }}>
                        {a.message}
                      </span>
                      <span style={{ color: '#737373', fontSize: 11 }}>
                        {timeAgo(a.createdAt)}
                      </span>
                    </>
                  }
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 600, color: '#F5F5F5' }}
                  secondaryTypographyProps={{ component: 'span' }}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Divider sx={{ borderColor: '#262626' }} />
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={markAllRead}
            disabled={alerts.length === 0}
            sx={{ color: '#60A5FA', textTransform: 'none' }}
          >
            Mark all read
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationsBell;
